import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type React from 'react';
import { getAvatarText } from '../../../common/avatars.js';
import { pickAvatarColor } from '../../../common/colors.js';
import { SimpleLink } from '../../../common/components/link/index.js';
import { Dropdown, useDropdownClose } from '../../../common/daisy/dropdown.js';
import getImageUrl from '../../../common/utils/get-image-url.js';
import type { Project } from '../../../project/index.js';
import type { ProjectMenuProps } from './project-menu-hook.js';
import UsefulSnippits from './useful-snippits/index.js';

export type { ProjectMenuProps };

export type ProjectMenuItemType = Omit<Project, 'schema'>;

export type AvailableProjectsListProps = {
  readonly items: ProjectMenuItemType[];
  readonly loading: boolean;
  readonly loaded: boolean;
  readonly currentProjectId?: string;
};

export type ProjectMenuItemProps = {
  readonly avatar: React.ReactElement;
  readonly label: string;
  readonly href: string;
};

function ProjectAvatar({ project }: { readonly project: Project }): JSX.Element {
  const avatarPath = project.avatar?.path;
  const avatarColor = pickAvatarColor(project.id);

  return (
    <div
      className="avatar rounded-2xl w-6 h-6 flex items-center justify-center text-white text-sm font-medium"
      style={{
        backgroundColor: avatarPath ? 'transparent' : avatarColor,
        backgroundImage: avatarPath ? `url(${getImageUrl(avatarPath, { w: 60, h: 60 })})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {!avatarPath && getAvatarText(project.name, 2)}
    </div>
  );
}

export const ProjectMenuItem: React.FC<ProjectMenuItemProps> = (props: ProjectMenuItemProps): JSX.Element => {
  const { avatar, label, href, ...rest } = props;
  const close = useDropdownClose();

  return (
    <SimpleLink href={href} {...rest}>
      <button type="button" className="flex items-center p-2 cursor-pointer w-full" onClick={close}>
        <div className="mr-2">{avatar}</div>
        <span className="text-gray-900 truncate">{label}</span>
      </button>
    </SimpleLink>
  );
};

const AvailableProjects: React.FC<
  AvailableProjectsListProps & {
    readonly filterText: string;
    readonly setFilterText: (str: string) => void;
  }
> = (props) => {
  if (props.loading) {
    return (
      <div className="flex justify-center p-4">
        <span className="loading loading-spinner loading-md text-primary" />
      </div>
    );
  }

  const filteredProjects = props.items.filter((project) => {
    if (project.id === props.currentProjectId) return false;
    return project.name.toLowerCase().includes(props.filterText.toLowerCase());
  });

  return (
    <div className="p-2">
      <div className="mb-2">
        <label className="input">
          <input
            type="text"
            placeholder="Filter projects..."
            value={props.filterText}
            onChange={(e) => props.setFilterText(e.target.value)}
          />
          {props.filterText && (
            <button
              type="button"
              className="flex items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                props.setFilterText('');
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </label>
      </div>
      {filteredProjects.map((project) => {
        const avatar = <ProjectAvatar project={project} />;
        return (
          <ProjectMenuItem key={project.id} avatar={avatar} label={project.name} href={`/project/${project.id}`} />
        );
      })}
      {filteredProjects.length === 0 && props.filterText && (
        <div className="text-gray-500 text-sm p-2 text-center">No projects found</div>
      )}
    </div>
  );
};

const ProjectMenuContent = ({
  currentProject,
  availableProjects,
  branchName,
  branchPath,
  filterText,
  setFilterText
}: {
  currentProject: Project;
  availableProjects: AvailableProjectsListProps;
  branchName: string;
  branchPath: string;
  filterText: string;
  setFilterText: (text: string) => void;
}) => {
  const close = useDropdownClose();

  return (
    <>
      {/* Top section: Available projects and Useful Snippets side by side */}
      <div className="flex flex-row flex-1 min-h-0">
        {/* Left section: Available projects - only show if there are other projects */}
        {availableProjects.items.filter((project: ProjectMenuItemType) => project.id !== currentProject?.id).length >
          0 && (
          <div data-testid="project-menu-options" className="w-64 border-r border-gray-200 h-full overflow-y-auto">
            <AvailableProjects
              {...availableProjects}
              currentProjectId={currentProject?.id}
              filterText={filterText}
              setFilterText={setFilterText}
            />
          </div>
        )}
        {/* Right section: Useful Snippets - full width when no other projects */}
        {currentProject && (
          <div
            className={`h-full overflow-y-auto ${
              availableProjects.items.filter((project: ProjectMenuItemType) => project.id !== currentProject.id)
                .length > 0
                ? 'w-96'
                : 'w-full'
            }`}
          >
            <UsefulSnippits projectId={currentProject.id} branch={branchName} branchPath={branchPath} onCopy={close} />
          </div>
        )}
      </div>
      {/* Bottom section: Create New Project button - full width */}
      <SimpleLink href="/projects">
        <div
          data-testid="create-project-button"
          className="flex items-center justify-center px-4 py-2 hover:bg-gray-50 cursor-pointer border-t border-gray-100 flex-shrink-0 w-full"
        >
          <AddIcon className="w-5 h-5 text-blue-600 mr-3" />
          <span className="text-gray-900">Create New Project</span>
        </div>
      </SimpleLink>
    </>
  );
};

export const ProjectMenu = (props: ProjectMenuProps) => {
  const { activeProject, currentProject, availableProjects, branchName, branchPath, filterText, setFilterText } = props;

  if (activeProject && currentProject) {
    return (
      <div className="h-full flex items-center max-w-96" id="project-menu">
        <Dropdown
          position="dropdown-bottom"
          dropdownClassName="menu p-0 shadow-xl bg-white rounded-lg border-gray-200 border flex-col h-96"
          trigger={
            <div
              data-testid="project-menu-button"
              className="btn btn-ghost border-none hover:bg-transparent btn-no-animation h-full flex items-center justify-start px-2 text-gray-900 rounded-lg font-normal cursor-pointer"
              aria-label="Active Project"
              tabIndex={0}
              role="button"
            >
              <ProjectAvatar project={currentProject} />
              <span className="ml-2 truncate max-w-48">{currentProject?.name}</span>
            </div>
          }
        >
          <ProjectMenuContent
            currentProject={currentProject}
            availableProjects={availableProjects}
            branchName={branchName}
            branchPath={branchPath}
            filterText={filterText}
            setFilterText={setFilterText}
          />
        </Dropdown>
      </div>
    );
  }

  return null;
};

export default ProjectMenu;
